import { API_URL } from '@/config';
import React, { useState, useEffect } from 'react';
import { Star, Upload, CheckCircle, Loader2, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { getImageUrl } from '@/utils/imageUrl';

const API = `${API_URL}/api`;

const EventRegistrationForm = ({ 
  event, 
  form, 
  bankDetails, 
  isAuthenticated = false,
  user = null,
  onSuccess,
  onClose 
}) => {
  const [formResponses, setFormResponses] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [paymentProofUrl, setPaymentProofUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [showTermsDialog, setShowTermsDialog] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [userData, setUserData] = useState({
    user_name: user?.name || '',
    user_email: user?.email || '',
    user_phone: user?.mobile || ''
  });

  useEffect(() => {
    if (user) {
      setUserData({
        user_name: user.name || '',
        user_email: user.email || '',
        user_phone: user.mobile || ''
      });
    }
  }, [user]);

  const handleInputChange = (fieldId, value) => {
    setFormResponses(prev => ({ ...prev, [fieldId]: value }));
  };

  const handleCheckboxChange = (fieldId, optionValue, checked) => {
    setFormResponses(prev => {
      const current = prev[fieldId] || [];
      if (checked) {
        return { ...prev, [fieldId]: [...current, optionValue] };
      } else {
        return { ...prev, [fieldId]: current.filter(v => v !== optionValue) };
      }
    });
  };

  const handleMultiSelectChange = (fieldId, optionValue, checked) => {
    handleCheckboxChange(fieldId, optionValue, checked);
  };

  const handleFileUpload = async (fieldId, file) => {
    if (!file) return;
    
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      
      const headers = { 'Content-Type': 'multipart/form-data' };
      if (isAuthenticated) {
        headers.Authorization = `Bearer ${localStorage.getItem('session_token')}`;
      }
      
      const response = await axios.post(`${API}/upload`, uploadFormData, { headers });
      handleInputChange(fieldId, response.data.url);
      toast.success('File uploaded');
    } catch (error) {
      toast.error('Failed to upload file');
    }
  };

  const handlePaymentProofUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      
      const headers = { 'Content-Type': 'multipart/form-data' };
      if (isAuthenticated) {
        headers.Authorization = `Bearer ${localStorage.getItem('session_token')}`;
      }
      
      const response = await axios.post(`${API}/upload`, uploadFormData, { headers });
      setPaymentProofUrl(response.data.url);
      toast.success('Payment proof uploaded');
    } catch (error) {
      toast.error('Failed to upload payment proof');
    } finally {
      setUploading(false);
    }
  };

  const validateForm = () => {
    // Validate user info
    if (!userData.user_name || !userData.user_email) {
      toast.error('Name and email are required');
      return false;
    }

    // Validate required fields
    for (const field of form.fields) {
      if (field.required && !formResponses[field.field_id]) {
        toast.error(`${field.label} is required`);
        return false;
      }
    }

    // Validate payment proof for paid events
    if (event.payment_type === 'paid' && event.registration_amount && !paymentProofUrl) {
      toast.error('Please upload payment proof');
      return false;
    }

    // Validate terms acceptance if required
    if (event.require_consent && !termsAccepted) {
      toast.error('Please accept the terms and conditions');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const payload = {
        form_id: form.form_id,
        event_id: event.event_id,
        user_name: userData.user_name,
        user_email: userData.user_email,
        user_phone: userData.user_phone,
        responses: formResponses,
        payment_proof_url: paymentProofUrl || null,
        terms_accepted: termsAccepted || false
      };

      const endpoint = isAuthenticated ? '/forms/submit/member' : '/forms/submit';
      const headers = isAuthenticated 
        ? { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        : {};

      await axios.post(`${API}${endpoint}`, payload, { headers });
      toast.success('Registration submitted successfully!');
      onSuccess?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit registration');
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field) => {
    switch (field.field_type) {
      case 'text':
      case 'email':
      case 'phone':
      case 'number':
        return (
          <Input
            type={field.field_type === 'phone' ? 'tel' : field.field_type}
            placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
            value={formResponses[field.field_id] || ''}
            onChange={(e) => handleInputChange(field.field_id, e.target.value)}
            required={field.required}
            data-testid={`field-${field.field_id}`}
          />
        );

      case 'date':
        return (
          <Input
            type="date"
            value={formResponses[field.field_id] || ''}
            onChange={(e) => handleInputChange(field.field_id, e.target.value)}
            required={field.required}
            data-testid={`field-${field.field_id}`}
          />
        );

      case 'textarea':
        return (
          <Textarea
            placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
            value={formResponses[field.field_id] || ''}
            onChange={(e) => handleInputChange(field.field_id, e.target.value)}
            required={field.required}
            rows={3}
            data-testid={`field-${field.field_id}`}
          />
        );

      case 'dropdown':
        return (
          <Select
            value={formResponses[field.field_id] || ''}
            onValueChange={(value) => handleInputChange(field.field_id, value)}
          >
            <SelectTrigger data-testid={`field-${field.field_id}`}>
              <SelectValue placeholder={field.placeholder || `Select ${field.label.toLowerCase()}`} />
            </SelectTrigger>
            <SelectContent>
              {field.options?.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case 'radio':
        return (
          <RadioGroup
            value={formResponses[field.field_id] || ''}
            onValueChange={(value) => handleInputChange(field.field_id, value)}
            data-testid={`field-${field.field_id}`}
          >
            {field.options?.map((opt) => (
              <div key={opt.value} className="flex items-center space-x-2">
                <RadioGroupItem value={opt.value} id={`${field.field_id}-${opt.value}`} />
                <Label htmlFor={`${field.field_id}-${opt.value}`}>{opt.label}</Label>
              </div>
            ))}
          </RadioGroup>
        );

      case 'checkbox':
      case 'multiselect':
        return (
          <div className="space-y-2" data-testid={`field-${field.field_id}`}>
            {field.options?.map((opt) => (
              <div key={opt.value} className="flex items-center space-x-2">
                <Checkbox
                  id={`${field.field_id}-${opt.value}`}
                  checked={(formResponses[field.field_id] || []).includes(opt.value)}
                  onCheckedChange={(checked) => handleCheckboxChange(field.field_id, opt.value, checked)}
                />
                <Label htmlFor={`${field.field_id}-${opt.value}`}>{opt.label}</Label>
              </div>
            ))}
          </div>
        );

      case 'file':
        return (
          <div>
            <Input
              type="file"
              onChange={(e) => handleFileUpload(field.field_id, e.target.files[0])}
              data-testid={`field-${field.field_id}`}
            />
            {formResponses[field.field_id] && (
              <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                <CheckCircle className="w-3 h-3" /> File uploaded
              </p>
            )}
          </div>
        );

      case 'rating':
        const maxRating = field.max_rating || 5;
        const currentRating = formResponses[field.field_id] || 0;
        return (
          <div className="flex gap-1" data-testid={`field-${field.field_id}`}>
            {Array.from({ length: maxRating }, (_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => handleInputChange(field.field_id, i + 1)}
                className="p-1 hover:scale-110 transition-transform"
              >
                <Star
                  className={`w-6 h-6 ${i < currentRating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
                />
              </button>
            ))}
          </div>
        );

      default:
        return (
          <Input
            placeholder={field.placeholder}
            value={formResponses[field.field_id] || ''}
            onChange={(e) => handleInputChange(field.field_id, e.target.value)}
          />
        );
    }
  };

  const isPaidEvent = event.payment_type === 'paid' && event.registration_amount;

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="event-registration-form">
      {/* User Info Section */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Your Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label>Full Name *</Label>
              <Input
                value={userData.user_name}
                onChange={(e) => setUserData({ ...userData, user_name: e.target.value })}
                placeholder="Enter your name"
                required
                disabled={isAuthenticated && user?.name}
                data-testid="registration-name-input"
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={userData.user_email}
                onChange={(e) => setUserData({ ...userData, user_email: e.target.value })}
                placeholder="Enter your email"
                required
                disabled={isAuthenticated && user?.email}
                data-testid="registration-email-input"
              />
            </div>
            <div className="sm:col-span-2">
              <Label>Phone</Label>
              <Input
                type="tel"
                value={userData.user_phone}
                onChange={(e) => setUserData({ ...userData, user_phone: e.target.value })}
                placeholder="Enter your phone number"
                data-testid="registration-phone-input"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Form Fields */}
      {form.fields && form.fields.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Registration Details</CardTitle>
            {form.description && <CardDescription>{form.description}</CardDescription>}
          </CardHeader>
          <CardContent className="space-y-4">
            {form.fields
              .sort((a, b) => a.order - b.order)
              .map((field) => (
                <div key={field.field_id}>
                  <Label className="mb-2 block">
                    {field.label}
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                  </Label>
                  {renderField(field)}
                </div>
              ))}
          </CardContent>
        </Card>
      )}

      {/* Payment Section */}
      {isPaidEvent && (
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              Payment Details
              <span className="text-amber-600 font-bold">₹{event.registration_amount}</span>
            </CardTitle>
            <CardDescription>Please make the payment and upload proof</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {bankDetails && (
              <div className="bg-white p-4 rounded-lg space-y-2">
                <p className="font-medium">{bankDetails.bank_name}</p>
                <p className="text-sm">A/C: {bankDetails.account_number}</p>
                <p className="text-sm">IFSC: {bankDetails.ifsc_code}</p>
                <p className="text-sm">Name: {bankDetails.account_name}</p>
                {bankDetails.upi_id && <p className="text-sm">UPI: {bankDetails.upi_id}</p>}
                {bankDetails.qr_code_url && (
                  <div className="mt-2">
                    <img 
                      src={getImageUrl(bankDetails.qr_code_url)} 
                      alt="QR Code" 
                      className="w-40 h-40 object-contain border rounded"
                    />
                  </div>
                )}
              </div>
            )}

            <div>
              <Label>Upload Payment Screenshot *</Label>
              <Input
                type="file"
                accept="image/*"
                onChange={(e) => handlePaymentProofUpload(e.target.files[0])}
                disabled={uploading}
                data-testid="payment-proof-upload"
              />
              {uploading && <p className="text-xs text-muted-foreground mt-1">Uploading...</p>}
              {paymentProofUrl && (
                <div className="mt-2">
                  <img 
                    src={getImageUrl(paymentProofUrl)} 
                    alt="Payment Proof" 
                    className="w-32 h-32 object-cover rounded border"
                  />
                  <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" /> Payment proof uploaded
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Terms and Conditions Checkbox - Always shown before submit */}
      <div className="flex items-start space-x-3 py-4 border-t">
        <Checkbox
          id="terms-checkbox"
          checked={termsAccepted}
          onCheckedChange={(checked) => setTermsAccepted(checked)}
          data-testid="terms-checkbox"
        />
        <Label 
          htmlFor="terms-checkbox" 
          className="text-sm leading-tight cursor-pointer"
        >
          By submitting, you agree to our{' '}
          <button
            type="button"
            onClick={() => setShowTermsDialog(true)}
            className="text-blue-600 hover:underline font-medium inline-flex items-center gap-1"
          >
            Terms and Conditions
            <ExternalLink className="w-3 h-3" />
          </button>
          <span className="text-red-500 ml-1">*</span>
        </Label>
      </div>

      {/* Submit Button */}
      <div className="flex gap-3">
        <Button
          type="submit"
          disabled={submitting || !termsAccepted}
          className="flex-1"
          style={{ backgroundColor: '#D4AF37', color: '#0F172A' }}
          data-testid="submit-registration-btn"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Submitting...
            </>
          ) : (
            event.cta_text || 'Submit Registration'
          )}
        </Button>
        {onClose && (
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
        )}
      </div>

      {/* Terms and Conditions Dialog */}
      <Dialog open={showTermsDialog} onOpenChange={setShowTermsDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Terms and Conditions</DialogTitle>
          </DialogHeader>
          <div className="prose prose-sm max-w-none">
            {event.terms_and_conditions ? (
              <div 
                className="text-sm text-muted-foreground"
                dangerouslySetInnerHTML={{ __html: event.terms_and_conditions }}
              />
            ) : (
              <div className="text-sm text-muted-foreground space-y-4">
                <p><strong>1. Acceptance of Terms</strong></p>
                <p>By registering for this event, you agree to comply with and be bound by these terms and conditions.</p>
                
                <p><strong>2. Registration</strong></p>
                <p>All information provided during registration must be accurate and complete. You are responsible for maintaining the confidentiality of your registration details.</p>
                
                <p><strong>3. Event Participation</strong></p>
                <p>Participation in the event is subject to the organizer's discretion. The organizer reserves the right to refuse or revoke registration at any time.</p>
                
                <p><strong>4. Code of Conduct</strong></p>
                <p>All participants are expected to conduct themselves in a professional and respectful manner during the event.</p>
                
                <p><strong>5. Privacy</strong></p>
                <p>Your personal information will be handled in accordance with our privacy policy. By registering, you consent to the collection and use of your information for event-related purposes.</p>
                
                <p><strong>6. Liability</strong></p>
                <p>The organizer is not liable for any loss, damage, or injury arising from participation in the event.</p>
                
                <p><strong>7. Changes</strong></p>
                <p>The organizer reserves the right to modify event details, including date, time, and venue, with reasonable notice.</p>
                
                <p><strong>8. Contact</strong></p>
                <p>For any queries regarding these terms, please contact the event organizer.</p>
              </div>
            )}
          </div>
          <div className="flex justify-end pt-4 border-t">
            <Button onClick={() => setShowTermsDialog(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </form>
  );
};

export default EventRegistrationForm;
